import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useUpdateRoles } from './useUpdateRoles'
import { useAccountAction } from './useAccountAction'
import { useDeleteUser } from './useDeleteUser'

import { useApp } from '../../contexts/AppContext'
import { useAuth } from '../../contexts/AuthContext'
import { useConfirm } from '../../contexts/ConfirmContext'
import {
  CONFIRM_ABORT,
  ConfirmDetail,
  ConfirmDetailType,
  ConfirmLayout,
} from '../../types/confirm/confirm'
import { BannerKind } from '../../types/contexts/AppContext'
import {
  AccountActionType,
  ADMIN_LOCK_REASONS,
  ProfileMetadata,
  UserLockReason,
} from '../../types/user/userProfile'
import { displayDate } from '../../utils/dateUtils'
import { getUserFullName, userStatusI18nKey } from '../../utils/users/user'
import { hasAnyRole, RoleType } from '../../utils/jwtUtils'
import { ActionType } from '../../constants/actionTypes'

const USERS_PATH = '/a/users'

type UseUserManagementOptions = {
  onRefresh?: () => void
  returnTo?: string
}

export const useUserManagement = ({
  onRefresh,
  returnTo = USERS_PATH,
}: UseUserManagementOptions = {}) => {
  const navigate = useNavigate()
  const { showBanner } = useApp()
  const { confirm } = useConfirm()
  const { authInfo, authenticatedUserId } = useAuth()
  const { t, i18n } = useTranslation('users')

  const {
    updateRoles,
    loading: updateRolesLoading,
    success: updateRolesSuccess,
    error: updateRolesError,
    resetState: resetUpdateRolesState,
  } = useUpdateRoles()

  const {
    performAccountAction,
    loading: accountActionLoading,
    success: accountActionSuccess,
    error: accountActionError,
    resetState: resetAccountActionState,
  } = useAccountAction()

  const {
    deleteUser,
    loading: deleteUserLoading,
    success: deleteUserSuccess,
    error: deleteUserError,
    resetState: resetDeleteUserState,
  } = useDeleteUser()

  const actionLoading =
    updateRolesLoading || accountActionLoading || deleteUserLoading

  const authenticatedUserIsAdmin = authInfo?.accessToken
    ? hasAnyRole(authInfo.accessToken, [RoleType.ADMIN])
    : false

  const resetState = useCallback(() => {
    resetUpdateRolesState()
    resetAccountActionState()
    resetDeleteUserState()
  }, [resetDeleteUserState, resetUpdateRolesState, resetAccountActionState])

  const runAccountAction = useCallback(
    async (
      user: ProfileMetadata,
      type: AccountActionType,
      selectedValues?: Record<string, string>
    ) => {
      const reason =
        type === AccountActionType.LOCK
          ? ((selectedValues?.reason as UserLockReason | undefined) ??
            UserLockReason.OTHER)
          : undefined
      const done = await performAccountAction(
        user.userId,
        type,
        reason,
        selectedValues?.details
      )

      if (done) {
        onRefresh?.()
      }

      return done
    },
    [onRefresh, performAccountAction]
  )

  const getUserConfirmDetails = useCallback(
    (user: ProfileMetadata): ConfirmDetail[] => {
      const roles =
        user.roleTypeList
          ?.map(role => t(`users.roles.${role.toLowerCase()}`))
          .join(', ') ?? t('users.list.unavailable')

      const joined = user.joinedAt
        ? displayDate(user.joinedAt, i18n.language)
        : t('users.list.unavailable')

      const status = user.status
        ? t(userStatusI18nKey(user.status))
        : t('users.list.unavailable')

      return [
        {
          label: t('users.list.roles'),
          value: roles,
        },
        {
          label: t('users.list.status'),
          value: user.locked ? t('users.status.locked') : status,
        },
        ...(user.locked && user.lockReason
          ? [
              {
                label: t('users.list.lock-reason'),
                value: t(`users.lock-confirmation.reasons.${user.lockReason}`),
              },
            ]
          : []),
        {
          label: t('users.list.joined'),
          value: joined,
        },
      ]
    },
    [i18n.language, t]
  )

  const confirmDelete = useCallback(
    (user: ProfileMetadata) => {
      const fullName = getUserFullName(user)

      confirm({
        layout: ConfirmLayout.DETAILS,
        title: t('users.delete-confirmation.title'),
        subtitle: t('users.delete-confirmation.subtitle'),
        confirmLabel: t('users.delete-confirmation.confirm'),
        backLabel: t('users.delete-confirmation.cancel'),
        returnTo,
        itemCards: [
          {
            id: user.id,
            title: fullName || user.email,
            subtitle: user.email,
            details: getUserConfirmDetails(user),
          },
        ],
        onConfirm: async () => {
          if (!(await deleteUser(user.userId))) {
            return CONFIRM_ABORT
          }

          onRefresh?.()
        },
      })
    },
    [confirm, deleteUser, getUserConfirmDetails, onRefresh, returnTo, t]
  )

  const detailsField = useCallback(
    (): ConfirmDetail => ({
      key: 'details',
      label: t('users.action-details-label'),
      value: '',
      type: ConfirmDetailType.TEXT,
    }),
    [t]
  )

  const confirmLockChange = useCallback(
    (user: ProfileMetadata) => {
      const locked = user.locked
      const prefix = locked
        ? 'users.unlock-confirmation'
        : 'users.lock-confirmation'
      const fullName = getUserFullName(user)
      const details = getUserConfirmDetails(user)

      if (!locked) {
        details.push({
          key: 'reason',
          label: t('users.lock-confirmation.reason-label'),
          value: UserLockReason.OTHER,
          type: ConfirmDetailType.SELECT,
          options: ADMIN_LOCK_REASONS.map(reason => ({
            value: reason,
            label: t(`users.lock-confirmation.reasons.${reason}`),
          })),
        })
      }

      details.push(detailsField())

      confirm({
        layout: ConfirmLayout.DETAILS,
        title: t(`${prefix}.title`),
        subtitle: t(`${prefix}.subtitle`),
        confirmLabel: t(`${prefix}.confirm`),
        backLabel: t(`${prefix}.cancel`),
        returnTo,
        itemCards: [
          {
            id: user.id,
            title: fullName || user.email,
            subtitle: user.email,
            details,
          },
        ],
        onConfirm: async (_, selectedValues) =>
          (await runAccountAction(
            user,
            locked ? AccountActionType.UNLOCK : AccountActionType.LOCK,
            selectedValues
          ))
            ? undefined
            : CONFIRM_ABORT,
      })
    },
    [
      confirm,
      detailsField,
      getUserConfirmDetails,
      returnTo,
      runAccountAction,
      t,
    ]
  )

  const confirmPasswordReset = useCallback(
    (user: ProfileMetadata) => {
      confirm({
        layout: ConfirmLayout.DETAILS,
        title: t('users.reset-password-confirmation.title'),
        subtitle: t('users.reset-password-confirmation.subtitle'),
        confirmLabel: t('users.reset-password-confirmation.confirm'),
        backLabel: t('users.reset-password-confirmation.cancel'),
        returnTo,
        itemCards: [
          {
            id: user.id,
            title: getUserFullName(user) || user.email,
            subtitle: user.email,
            details: [...getUserConfirmDetails(user), detailsField()],
          },
        ],
        onConfirm: async (_, selectedValues) =>
          (await runAccountAction(
            user,
            AccountActionType.RESET_PASSWORD,
            selectedValues
          ))
            ? undefined
            : CONFIRM_ABORT,
      })
    },
    [
      confirm,
      detailsField,
      getUserConfirmDetails,
      returnTo,
      runAccountAction,
      t,
    ]
  )

  const handleRolesChange = useCallback(
    async (user: ProfileMetadata, roles: string[]) => {
      const validRoles = roles.filter((role): role is RoleType =>
        Object.values(RoleType).includes(role as RoleType)
      )

      if (!validRoles.includes(RoleType.USER)) {
        showBanner({
          kind: BannerKind.WARN,
          msg: t('users.warning.user-role-required'),
        })

        return
      }

      const removesOwnAdminRole =
        String(user.userId) === String(authenticatedUserId) &&
        authenticatedUserIsAdmin &&
        !validRoles.includes(RoleType.ADMIN)

      if (removesOwnAdminRole) {
        showBanner({
          kind: BannerKind.WARN,
          msg: t('users.warning.own-admin-role'),
        })

        return
      }

      if (await updateRoles(Number(user.id), validRoles)) {
        onRefresh?.()
      }
    },
    [
      authenticatedUserId,
      authenticatedUserIsAdmin,
      onRefresh,
      showBanner,
      t,
      updateRoles,
    ]
  )

  const handleAction = useCallback(
    async (user: ProfileMetadata, action: ActionType, roles?: string[]) => {
      const targetsAuthenticatedUser =
        String(user.userId) === String(authenticatedUserId)

      switch (action) {
        case ActionType.VIEW:
          navigate(`/a/users/${user.id}`)
          return
        case ActionType.UPDATE:
          await handleRolesChange(user, roles ?? [])
          return
        case ActionType.LOCK:
          if (targetsAuthenticatedUser) return
          confirmLockChange(user)
          return
        case ActionType.RESET_PASSWORD:
          confirmPasswordReset(user)
          return
        case ActionType.DELETE:
          if (targetsAuthenticatedUser) return
          confirmDelete(user)
          return
      }
    },
    [
      authenticatedUserId,
      confirmDelete,
      confirmLockChange,
      confirmPasswordReset,
      handleRolesChange,
      navigate,
    ]
  )

  useEffect(() => {
    const errors = [updateRolesError, accountActionError, deleteUserError]
    const successes = [
      updateRolesSuccess,
      accountActionSuccess,
      deleteUserSuccess,
    ]

    errors.forEach(error => {
      if (error) {
        showBanner({
          kind: BannerKind.ERR,
          msg: error,
        })
      }
    })

    successes.forEach(success => {
      if (success) {
        showBanner({
          kind: BannerKind.OK,
          msg: success,
        })
      }
    })

    resetState()
  }, [
    deleteUserError,
    deleteUserSuccess,
    resetState,
    showBanner,
    updateRolesError,
    updateRolesSuccess,
    accountActionError,
    accountActionSuccess,
  ])

  return {
    actionLoading,
    handleAction,
  }
}
